import { isNodeReservedKeyword } from "@utils/node/keywords"

export const errMsg = {
  LABEL_CANNOT_CONTAIN_BRACES: "Label cannot contain { or }",
  LABEL_CANNOT_START_WITH_NUMBER: "Label cannot start with a number",
  LABEL_CANNOT_CONTAIN_DOUBLE_UNDERSCORE: 'Label cannot contain double underscore "__"',
  LABEL_CANNOT_CONTAIN_LAG: 'Label cannot contain " lag"',
  LABEL_RESERVED_KEYWORD: (label: string) => `Label ${label} is reserved keyword`,
  LABEL_CANNOT_BE_TO_LONG: "Label cannot be longer that 245 characters",
  LABEL_CANNOT_BE_EMPTY: "Label cannot be empty",
  LABEL_CONTAINS_INVALID_CHARACTERS: (characters: Iterable<string>) =>
    `Label contains invalid characters: ${Array.from(characters).join(" ")}`,
}

export const LAG_LABEL_REGEX = /[Ll]ag ?[0-9]+$/
export const LAG_LABEL_REGEX_GLOBAL = /[Ll]ag ?[0-9]+$/g

const REPEATED_LAG_SUFFIX_REGEX = /.+\s[Ll]ag(?: ?[0-9]+)?$/

export const createLagNodeLabel = (label: string | undefined, lag: number): string => {
  return `${label ?? ""} Lag ${lag}`
}

export const getLabelWithoutLagSuffix = (label: string): string => {
  return label.replace(LAG_LABEL_REGEX, "").trimEnd()
}

export const splitLagNodeLabel = (label?: string) => {
  const labelValue = label ?? ""
  const lagSuffix = labelValue.match(LAG_LABEL_REGEX)?.[0]

  if (!lagSuffix) {
    return {
      label: labelValue,
      lagValue: 1,
    }
  }

  return {
    label: getLabelWithoutLagSuffix(labelValue),
    lagValue: Number(lagSuffix.replace(/^[Ll]ag ?/, "")),
  }
}

/**
 * Validates generic node-label rules against the full label by default.
 */
export const getNodeLabelValidationErrors = (
  label: string | undefined,
  options?: {
    /**
     * When `allowLagSuffix` is true, strips one terminal `Lag <number>` suffix
     * first and validates the remaining base label. Forms validate an editable
     * base label; persisted lag nodes use the stripped-label mode.
     */
    allowLagSuffix?: boolean
  },
): string[] => {
  const trimmedLabel = label?.trim()
  if (!trimmedLabel) return []

  const labelToValidate = options?.allowLagSuffix
    ? getLabelWithoutLagSuffix(trimmedLabel)
    : trimmedLabel
  const errors: string[] = []

  if (/^[0-9]/.test(labelToValidate)) {
    errors.push(errMsg.LABEL_CANNOT_START_WITH_NUMBER)
  }

  const invalidCharacterMatches = labelToValidate.match(/[^A-Za-z0-9_() +/\-]/g)
  if (invalidCharacterMatches) {
    errors.push(errMsg.LABEL_CONTAINS_INVALID_CHARACTERS(new Set(invalidCharacterMatches)))
  }

  if (labelToValidate.includes("__")) {
    errors.push(errMsg.LABEL_CANNOT_CONTAIN_DOUBLE_UNDERSCORE)
  }

  const containsLag = labelToValidate.toLowerCase().includes(" lag")
  const hasRepeatedLagSuffix = REPEATED_LAG_SUFFIX_REGEX.test(labelToValidate)

  if ((!options?.allowLagSuffix && containsLag) || hasRepeatedLagSuffix) {
    errors.push(errMsg.LABEL_CANNOT_CONTAIN_LAG)
  }

  if (isNodeReservedKeyword(labelToValidate)) {
    errors.push(errMsg.LABEL_RESERVED_KEYWORD(trimmedLabel))
  }

  if (!label) {
    errors.push(errMsg.LABEL_CANNOT_BE_EMPTY)
  }

  if (label && label.length >= 245) {
    errors.push(errMsg.LABEL_CANNOT_BE_TO_LONG)
  }

  return errors
}

export const isValidLagNodeLabel = (label: string | undefined): boolean => {
  if (!label || !LAG_LABEL_REGEX.test(label)) return false

  return !REPEATED_LAG_SUFFIX_REGEX.test(getLabelWithoutLagSuffix(label))
}

export const normalizeNodeLabel = (label: string | undefined): string => {
  return label?.trim().toLowerCase() ?? ""
}

export const isNodeLabelUnique = (
  label: string | undefined,
  existingLabels: (string | undefined)[],
): boolean => {
  const normalizedLabel = normalizeNodeLabel(label)

  return (
    normalizedLabel === "" ||
    !existingLabels.some((existingLabel) => normalizeNodeLabel(existingLabel) === normalizedLabel)
  )
}
